// Constants and utils
import { hashPassword } from '@shopee-user/business-customization-logic';
import { formatPhoneNumberForServer } from '@shopee-user/authentication-utils';
import { PaginationOrder } from '@shopee-user/account-constants';
import { post } from '@shopee-user/fetch-utils';
import { post as postV2 } from '@shopee-user/fetch-utils-v2';
import { formatResponseDataField } from '@shopee-user/schema-formatter';
import {
  changeEmailResponseSchema,
  changeEmailInitResponseSchema,
  changePhoneResponseSchema,
  getIntegrationLinkListResponseSchema,
} from './schema';

// Types
import type { APIResponse } from '@shopee-user/api-constants';

import type {
  ChangePasswordResponse,
  ChangeStaffPasswordResponse,
  VerifyPasswordResponse,
  FetchAccountResponse,
  FetchStaffAccountListResponse,
  FetchStaffAccountResponse,
  AddPasswordResponse,
  FetchLoginStatusResponse,
  AddPhoneResponse,
  AddEmailResponse,
  ChangeStaffPhoneResponse,
  ChangePhoneInitResponse,
} from './types';
import { ThirdpartyBindingEntryPoint } from '@tob/shared/thirdparty-auth/service/binding/constants';

// This API is for self-updated password
// For example Admin update Admin password, Staff update Staff password
export async function changePassword({
  password,
  faceVerificationId,
}: {
  password: string;
  faceVerificationId?: number;
}): Promise<APIResponse<ChangePasswordResponse>> {
  const hashedPassword = await hashPassword(password);
  return post('/api/v4/account/business/change_password', {
    new_password: hashedPassword,
    face_verification_id: faceVerificationId,
  });
}

export async function addPassword({
  password,
}: {
  password: string;
}): Promise<APIResponse<AddPasswordResponse>> {
  const hashedPassword = await hashPassword(password);
  return post('/api/v4/account/business/add_password', {
    new_password: hashedPassword,
  });
}

// This API is for Admin change Staff Password
export async function changeStaffPassword({
  staffId,
  password,
}: {
  staffId: string;
  password: string;
}): Promise<APIResponse<ChangeStaffPasswordResponse>> {
  const hashedPassword = await hashPassword(password);
  return post('/api/v4/account/business/change_staff_password', {
    staff_userid: parseInt(staffId, 10),
    new_password: hashedPassword,
  });
}

// This API is for Admin to change Staff Phone
export async function changeStaffPhone({
  staffId,
  phone,
}: {
  staffId: string;
  phone: string;
}): Promise<APIResponse<ChangeStaffPhoneResponse>> {
  const formattedPhoneNumber = await formatPhoneNumberForServer(phone);
  return post('/api/v4/account/business/change_staff_phone', {
    staff_userid: parseInt(staffId, 10),
    new_phone: formattedPhoneNumber,
  });
}

// This API is for Verifying Admin/Staff Password before updating password
export async function verifyPassword({
  password,
  shouldSendPlainPassword,
}: {
  password: string;
  shouldSendPlainPassword: boolean;
}): Promise<APIResponse<VerifyPasswordResponse>> {
  const hashedPassword = await hashPassword(password);
  return post('/api/v4/account/business/verify_password', {
    password: hashedPassword,
    shouldSendPlainPassword,
  });
}

// This API is for fetch account details
export async function fetchAccount(): Promise<
  APIResponse<FetchAccountResponse>
> {
  return post('/api/v4/account/business/get_business_account', {});
}

// This API is to get login status, which must be called at the
// beginning of authentication routes
export async function fetchLoginStatus(): Promise<
  APIResponse<FetchLoginStatusResponse>
> {
  return post('/api/v4/account/business/login_status', {});
}

// This API is for admin to fetch staff account details
export async function fetchStaffAccount({
  staffId,
}: {
  staffId: string;
}): Promise<APIResponse<FetchStaffAccountResponse>> {
  return post('/api/v4/account/business/get_staff_account', {
    staff_userid: parseInt(staffId, 10),
  });
}

// This API is for admin to fetch staff account list
export async function fetchStaffAccountList({
  lastUserId,
  limit,
}: {
  lastUserId: number | null | undefined;
  limit: number;
}): Promise<APIResponse<FetchStaffAccountListResponse>> {
  const request = {
    paging: {
      limit,
      cursor: [
        {
          field: 'userid',
          order: PaginationOrder.Ascending,
          last_value: lastUserId === null ? null : String(lastUserId),
        },
      ],
    },
  };
  return post('/api/v4/account/business/scan_staff_account', request);
}

/**
 * For addPhone/addEmail API, phone/email is encoded inside otp_token as a result
 * of verifying OTP using said phone/email. Therefore, it is not necessary to
 * supply phone/email to these APIs
 */

export async function addPhone({
  otpToken,
}: {
  otpToken: string;
}): Promise<APIResponse<AddPhoneResponse>> {
  return post('/api/v4/account/business/add_phone', {
    otp_token: otpToken,
  });
}

export async function addEmail({
  otpToken,
}: {
  otpToken: string;
}): Promise<APIResponse<AddEmailResponse>> {
  return post('/api/v4/account/business/add_email', {
    otp_token: otpToken,
  });
}

export async function changePhoneInit(): Promise<
  APIResponse<ChangePhoneInitResponse>
> {
  return postV2('/api/v4/account/business/change_phone_init', {});
}

export async function changePhone({
  otpToken,
  faceVerificationId,
}: {
  otpToken: string;
  faceVerificationId?: number;
}) {
  return formatResponseDataField(
    post('/api/v4/account/business/change_phone', {
      otp_token: otpToken,
      face_verification_id: faceVerificationId,
    }),
    changePhoneResponseSchema
  );
}

export async function changeEmail({
  otpToken,
  faceVerificationId,
}: {
  otpToken: string;
  faceVerificationId?: number;
}) {
  return formatResponseDataField(
    postV2('/api/v4/account/business/change_email', {
      otp_token: otpToken,
      face_verification_id: faceVerificationId,
    }),
    changeEmailResponseSchema
  );
}

export async function changeEmailInit() {
  return formatResponseDataField(
    postV2('/api/v4/account/business/change_email_init', {}),
    changeEmailInitResponseSchema
  );
}

export async function checkAccountExistByPassword({
  email,
}: {
  email: string;
}) {
  return postV2('/api/v4/account/business/check_account_exist_by_password', {
    email,
  });
}

// POST /api/v4/account/business/get_integration_link_list
export async function getIntegrationLinkList() {
  return formatResponseDataField(
    postV2('/api/v4/account/business/get_integration_link_list', {}),
    getIntegrationLinkListResponseSchema
  );
}

export async function bindShopee({
  entryPoint,
  authCode,
  clientId,
}: {
  entryPoint: ThirdpartyBindingEntryPoint;
  authCode: string;
  clientId: string;
}) {
  return postV2(
    '/api/v4/account/business/bind_shopee',
    {
      entry_point: entryPoint,
      shopee_auth_code: authCode,
      client_id: clientId,
    },
    {
      antiFraudConfig: {
        deviceFingerPrint: {
          dataFormatter: dfp => ({ security_device_fingerprint: dfp }),
        },
      },
    }
  );
}

export async function unbindShopee({
  entryPoint,
}: {
  entryPoint: ThirdpartyBindingEntryPoint;
}) {
  return postV2(
    '/api/v4/account/business/unbind_shopee',
    {
      entry_point: entryPoint,
    },
    {
      antiFraudConfig: {
        deviceFingerPrint: {
          dataFormatter: dfp => ({ security_device_fingerprint: dfp }),
        },
      },
    }
  );
}
